<template>
  <div class="spd" @click="clearActiveTag">

    <!-- ── Header / toggle bar ─────────────────────────────────── -->
    <button
      class="spd__header"
      :class="{ 'spd__header--collapsed': !isExpanded }"
      :aria-expanded="isExpanded.toString()"
      aria-controls="spd-body"
      @click.stop="toggleExpanded"
    >
      <div class="spd__header-left">
        <p class="spd__eyebrow">Media Sentiment Pulse</p>
        <h2 class="spd__title">{{ categoryLabel }}</h2>
        <p v-if="!isExpanded && collapsedSummary" class="spd__collapsed-summary">
          {{ collapsedSummary }}
        </p>
      </div>
      <div class="spd__header-right">
        <span v-if="pulse" class="spd__updated">
          <span class="spd__live-dot" aria-hidden="true"></span>
          Updated Just Now
        </span>
        <span class="spd__chevron" :class="{ 'spd__chevron--open': isExpanded }" aria-hidden="true">
          <i class="fa fa-chevron-down"></i>
        </span>
      </div>
    </button>

    <!-- ── Collapsible body ─────────────────────────────────────── -->
    <div id="spd-body" v-show="isExpanded">

    <!-- ── Loading skeletons ────────────────────────────────────── -->
    <div v-if="status === 'loading'" class="spd__grid">
      <div class="spd__card spd__card--coverage spd__skeleton">
        <div class="spd__sk spd__sk--short"></div>
        <div class="spd__sk spd__sk--long"></div>
        <div class="spd__sk spd__sk--long"></div>
        <div class="spd__sk spd__sk--mid"></div>
      </div>
      <div class="spd__card spd__card--sentiment spd__skeleton">
        <div class="spd__sk spd__sk--short"></div>
        <div class="spd__sk spd__sk--score"></div>
        <div class="spd__sk spd__sk--mid"></div>
      </div>
      <div class="spd__card spd__card--topics spd__skeleton">
        <div class="spd__sk spd__sk--short"></div>
        <div class="spd__sk-cloud">
          <div v-for="n in 12" :key="n" class="spd__sk spd__sk--tag" :style="{ width: (40 + Math.random() * 60) + 'px' }"></div>
        </div>
      </div>
      <div class="spd__card spd__card--story spd__skeleton">
        <div class="spd__sk spd__sk--short"></div>
        <div class="spd__sk spd__sk--long"></div>
        <div class="spd__sk spd__sk--long"></div>
        <div class="spd__sk spd__sk--mid"></div>
      </div>
    </div>

    <!-- ── Error ────────────────────────────────────────────────── -->
    <div v-else-if="status === 'error'" class="spd__error">
      <i class="fa fa-triangle-exclamation" aria-hidden="true"></i>
      Failed to load sentiment data. Please try again later.
    </div>

    <!-- ── Dashboard ────────────────────────────────────────────── -->
    <div v-else class="spd__grid">

      <div class="spd__card spd__card--coverage">
        <p class="spd__card-eyebrow">{{ politicsMode ? 'Article Framing: Last 24 Hours' : 'Coverage Tone: Last 24 Hours' }}</p>
        <p class="spd__card-total">{{ pulse.coverageTotal.toLocaleString() }} articles analyzed</p>
        <p
          v-if="politicsMode"
          class="spd__card-hint"
          tabindex="0"
          :title="framingMixTooltip"
        >
          {{ framingMixTooltip }}
        </p>

        <!-- Non-politics: horizontal stacked bar -->
        <template v-if="!politicsMode">
          <div class="spd__stacked-bar" role="img" :aria-label="`Coverage tone: ${coverageMixSegments.map(s => s.label + ' ' + s.pct + '%').join(', ')}`">
            <div
              v-for="seg in coverageMixSegments"
              :key="seg.label"
              class="spd__stack-seg"
              :style="{ width: seg.pct + '%', background: seg.color }"
            ></div>
          </div>
          <div class="spd__stacked-legend">
            <div v-for="seg in coverageMixSegments" :key="seg.label" class="spd__legend-item">
              <span class="spd__legend-dot" :style="{ background: seg.color }"></span>
              <span class="spd__legend-label">{{ capitalize(seg.label) }}</span>
              <span class="spd__legend-count">{{ seg.count }} &middot; {{ seg.pct }}%</span>
            </div>
          </div>
        </template>

        <!-- Politics: individual bars in spectrum order (incl. insufficient, never merged into center) -->
        <template v-else>
          <div
            class="spd__bias-bars"
            role="img"
            :aria-label="framingMixAria"
          >
            <div v-for="seg in coverageMixSegments" :key="seg.label" class="spd__bias-row">
              <span class="spd__bias-bar-label">{{ seg.label }}</span>
              <div class="spd__bias-track">
                <div class="spd__bias-fill" :style="{ width: seg.pct + '%', background: seg.color }"></div>
              </div>
              <span class="spd__bias-bar-count">{{ seg.count }}&nbsp;<span class="spd__bias-bar-pct">({{ seg.pct }}%)</span></span>
            </div>
          </div>
          <p
            v-if="provenanceFooter"
            class="spd__provenance"
            tabindex="0"
            :title="provenanceFooter"
          >
            {{ provenanceFooter }}
          </p>
        </template>
      </div>

    
      <div class="spd__card spd__card--sentiment">
        <p class="spd__card-eyebrow">Sentiment Overview</p>

        <!-- Single category view -->
        <template v-if="!isGlobal && singleSentiment">
          <div class="spd__big-score" :style="{ color: sentimentColor(singleSentiment.avgSentiment) }">
            {{ Math.round(singleSentiment.avgSentiment) }}
          </div>
          <span
            class="spd__big-badge"
            :style="{ background: sentimentColorBg(singleSentiment.avgSentiment), color: sentimentColor(singleSentiment.avgSentiment) }"
          >{{ sentimentLabel(singleSentiment.avgSentiment) }}</span>
          <p class="spd__big-subtitle">Based on {{ singleSentiment.articleCount }} articles in the last 24 hours</p>

          <!-- Framing avg meter — politics mode only; hide when avgBias null -->
          <template v-if="politicsMode && singleSentiment && singleSentiment.avgBias !== null && singleSentiment.avgBias !== undefined">
            <p class="spd__meter-title">Average Article Framing</p>
            <div class="spd__bias-meter">
              <div class="spd__bias-meter-track">
                <div class="spd__bias-meter-fill" :style="{ width: clampBias(singleSentiment.avgBias) + '%' }"></div>
                <div class="spd__bias-meter-marker" :style="{ left: clampBias(singleSentiment.avgBias) + '%' }"></div>
              </div>
              <div class="spd__bias-meter-scale">
                <span>Far Left</span>
                <span>Center</span>
                <span>Far Right</span>
              </div>
              <p class="spd__bias-meter-value">
                Score: {{ Math.round(clampBias(singleSentiment.avgBias)) }} / 100
                <span v-if="insufficientCountDisplay != null" class="spd__bias-meter-note">
                  · {{ insufficientCountDisplay }} insufficient
                </span>
              </p>
            </div>
          </template>
          <p
            v-else-if="politicsMode && insufficientCountDisplay != null"
            class="spd__bias-meter-note spd__bias-meter-note--solo"
          >
            {{ insufficientCountDisplay }} articles: insufficient evidence
          </p>
        </template>

        <!-- Global view: multiple category rows -->
        <template v-else>
          <div class="spd__cat-list">
            <div v-for="cat in pulse.categorySentiment" :key="cat.category" class="spd__cat-row">
              <span class="spd__cat-name">{{ capitalize(cat.category) }}</span>
              <span
                class="spd__cat-badge"
                :style="{ background: sentimentColorBg(cat.avgSentiment), color: sentimentColor(cat.avgSentiment) }"
              >{{ sentimentLabel(cat.avgSentiment) }} · {{ Math.round(cat.avgSentiment) }}</span>
              <span class="spd__cat-count">{{ cat.articleCount }}</span>
            </div>
          </div>
        </template>
      </div>

      
      <div class="spd__card spd__card--topics" @click.stop>
        <p class="spd__card-eyebrow">What People Are Talking About</p>

        <div v-if="pulse.topicCloud && pulse.topicCloud.length" class="spd__tag-cloud">
          <span
            v-for="(tag, i) in pulse.topicCloud"
            :key="tag.tag"
            class="spd__tag"
            :class="{
              'spd__tag--active': activeTag === i,
              'spd__tag--auth': !!currentUser,
              'spd__tag--followed': currentUser && tag.followed,
            }"
            role="button"
            tabindex="0"
            :style="chipStyle(tag)"
            :aria-label="currentUser ? (tag.followed ? `Unfollow ${tag.tag}` : `Follow ${tag.tag}`) : tag.tag"
            @click.stop="toggleFollowTag($event, i)"
            @keydown.enter.prevent="toggleFollowTag($event, i)"
          >
            {{ tag.tag }}
            <span v-if="currentUser" class="spd__tag-follow-icon" aria-hidden="true">
              <i :class="tag.followed ? 'fa fa-check' : 'fa fa-plus'"></i>
            </span>
          </span>
        </div>
        <p v-else class="spd__empty">No topic data available for this period.</p>
      </div>

     
      <div class="spd__card spd__card--story">
        <p class="spd__card-eyebrow">{{ pulse.storyWidgetLabel }}</p>

        <template v-if="pulse.mostPolarizingStory">
          <router-link
            :to="pulse.mostPolarizingStory.internalUrl"
            class="spd__story-title"
          >{{ pulse.mostPolarizingStory.title }}</router-link>
          <p class="spd__story-source">
            {{ pulse.mostPolarizingStory.sourceName }}<i class="fa fa-external-link-alt spd__story-link-icon" aria-hidden="true"></i>
          </p>
          <div class="spd__chips">
            <span
              class="spd__chip"
              :style="{ background: sentimentColorBg(pulse.mostPolarizingStory.sentimentScore), color: sentimentColor(pulse.mostPolarizingStory.sentimentScore) }"
            >{{ sentimentLabel(pulse.mostPolarizingStory.sentimentScore) }}</span>
            <span
              v-if="politicsMode && pulse.mostPolarizingStory.biasLabel"
              class="spd__chip"
              :style="biasChipStyle(pulse.mostPolarizingStory.biasLabel)"
            >{{ pulse.mostPolarizingStory.biasLabel }}</span>
          </div>
        </template>
        <p v-else class="spd__empty">No story data available for this period.</p>
      </div>

    </div>

    </div><!-- /spd-body -->

    <!-- ── Global tag tooltip — only shown for anonymous users ─── -->
    <div
      v-if="!currentUser && activeTag !== null && tooltipPos && pulse && pulse.topicCloud"
      class="spd__global-tooltip"
      :class="{ 'spd__global-tooltip--above': tooltipPos.showAbove }"
      :style="{
        top:  tooltipPos.top  + 'px',
        left: tooltipPos.left + 'px',
        '--spd-arrow-left': tooltipPos.arrowLeft + 'px',
      }"
    >
      <strong>{{ pulse.topicCloud[activeTag].count }}</strong> articles
      &middot; Avg sentiment: <strong>{{ pulse.topicCloud[activeTag].avgSentiment }}</strong>
    </div>

    <!-- ── Follow toast ──────────────────────────────────────────── -->
    <transition name="spd-toast">
      <div
        v-if="followToast"
        class="spd__toast"
        :class="{ 'spd__toast--error': followToast.isError }"
        role="status"
        aria-live="polite"
      >
        {{ followToast.message }}
      </div>
    </transition>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import ArticleDataService from '@/services/ArticleDataService';
import { FRAMING_TOOLTIP } from '@/utils/framingHelpers';

const BIAS_COLORS = {
  'strongly left':  { text: '#1d4ed8', bg: '#dbeafe' },
  'leans left':     { text: '#2563eb', bg: '#eff6ff' },
  'center':         { text: '#374151', bg: '#f3f4f6' },
  'leans right':    { text: '#dc2626', bg: '#fff1f2' },
  'strongly right': { text: '#991b1b', bg: '#fee2e2' },
  'insufficient evidence': { text: '#4b5563', bg: '#e5e7eb' },
};

const SPECTRUM_ORDER = [
  'strongly left',
  'leans left',
  'center',
  'leans right',
  'strongly right',
  'insufficient evidence',
];

const DIRECTIONAL_SPECTRUM = [
  'strongly left',
  'leans left',
  'center',
  'leans right',
  'strongly right',
];

const BIAS_BAR_COLORS = {
  'strongly left':  '#1d4ed8',
  'leans left':     '#60a5fa',
  'center':         '#9ca3af',
  'leans right':    '#f87171',
  'strongly right': '#dc2626',
  'insufficient evidence': '#9ca3af',
};

const SENTIMENT_ORDER = ['negative', 'neutral', 'positive'];
const SENTIMENT_COLORS = { negative: '#ef4444', neutral: '#eab308', positive: '#22c55e' };

export default {
  name: 'SentimentPulseDashboard',
  props: {
    category: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      pulse: null,
      status: 'loading',
      activeTag: null,
      tooltipPos: null,
      tick: 0,
      tickInterval: null,
      isExpanded: true,
      followToast: null,
      framingMixTooltip: FRAMING_TOOLTIP,
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.user.id,
    }),
    politicsMode() {
      return this.pulse?.politicsMode ?? false;
    },
    isGlobal() {
      return this.pulse?.category === null;
    },
    categoryLabel() {
      if (!this.pulse) return '--';
      if (!this.pulse.category) return 'Global Overview';
      return this.pulse.category
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    minutesAgo() {
      // Depend on tick so it recomputes every 60 s
      // eslint-disable-next-line no-unused-expressions
      this.tick;
      if (!this.pulse?.generatedAt) return '--';
      const diff = Math.floor((Date.now() - new Date(this.pulse.generatedAt).getTime()) / 60000);
      return Math.max(0, diff);
    },
    singleSentiment() {
      return this.pulse?.categorySentiment?.[0] ?? null;
    },
    insufficientCountDisplay() {
      const root = this.pulse?.insufficientCount;
      const cat = this.singleSentiment?.insufficientCount;
      const n = root != null ? Number(root) : (cat != null ? Number(cat) : null);
      if (n == null || !Number.isFinite(n) || n <= 0) return null;
      return n;
    },
    provenanceFooter() {
      if (!this.politicsMode || !this.pulse) return null;
      const parts = [];
      const pc = this.pulse.provenanceCounts;
      if (pc && typeof pc === 'object') {
        const v6 = Number(pc.v6) || 0;
        const legacy = Number(pc.legacy) || 0;
        if (v6 > 0 || legacy > 0) {
          parts.push(`Model mix: ${v6} V6 · ${legacy} legacy`);
        }
      }
      if (this.insufficientCountDisplay != null) {
        parts.push(`${this.insufficientCountDisplay} insufficient evidence`);
      }
      return parts.length ? parts.join(' · ') : null;
    },
    framingMixAria() {
      if (!this.coverageMixSegments.length) return 'Article framing distribution unavailable';
      return 'Article framing: ' + this.coverageMixSegments
        .map(s => `${s.label} ${s.count} (${s.pct} percent)`)
        .join(', ');
    },
    /**
     * Share of directional (non-insufficient) articles that are left/center/right-leaning.
     * Insufficient is excluded from this denominator — never treated as Center.
     */
    directionalShareNote() {
      if (!this.politicsMode || !this.pulse?.coverageMix) return null;
      const mix = this.pulse.coverageMix;
      let directionalTotal = 0;
      for (const k of DIRECTIONAL_SPECTRUM) {
        directionalTotal += Number(mix[k]) || 0;
      }
      if (directionalTotal <= 0) return null;
      const insuff = Number(mix['insufficient evidence']) || 0;
      if (insuff <= 0) return null;
      return `${insuff} excluded from directional ratios (${directionalTotal} directional)`;
    },
    collapsedSummary() {
      if (!this.pulse) return null;
      const parts = [];
      if (this.singleSentiment) {
        const score = Math.round(this.singleSentiment.avgSentiment);
        const label = score <= 33 ? 'Negative' : score <= 66 ? 'Neutral' : 'Positive';
        parts.push(`${label} sentiment (${score})`);
      }
      if (this.pulse.coverageTotal) {
        parts.push(`${this.pulse.coverageTotal} articles`);
      }
      return parts.join(' · ');
    },
    coverageMixSegments() {
      if (!this.pulse?.coverageMix) return [];
      const total = this.pulse.coverageTotal || 1;
      if (this.politicsMode) {
        // Show all six buckets when present. Pct is of full mix (distribution view).
        // Insufficient stays its own gray bucket — never merged into center.
        return SPECTRUM_ORDER
          .filter(k => k in this.pulse.coverageMix)
          .map(k => ({
            label: k,
            count: this.pulse.coverageMix[k],
            pct: Math.round((this.pulse.coverageMix[k] / total) * 100),
            color: BIAS_BAR_COLORS[k] || '#9ca3af',
            isInsufficient: k === 'insufficient evidence',
          }));
      }
      return SENTIMENT_ORDER
        .filter(k => k in this.pulse.coverageMix)
        .map(k => ({
          label: k,
          count: this.pulse.coverageMix[k],
          pct: Math.round((this.pulse.coverageMix[k] / total) * 100),
          color: SENTIMENT_COLORS[k],
        }));
    },
  },
  async mounted() {
    this.isExpanded = false;
    await this.fetchPulse();
    this.tickInterval = setInterval(() => { this.tick += 1; }, 60000);
    document.addEventListener('click', this.clearActiveTag);
    window.addEventListener('scroll', this.clearActiveTag, { passive: true });
  },
  beforeUnmount() {
    clearInterval(this.tickInterval);
    document.removeEventListener('click', this.clearActiveTag);
    window.removeEventListener('scroll', this.clearActiveTag);
  },
  watch: {
    category() {
      this.fetchPulse();
    },
  },
  methods: {
    async fetchPulse() {
      this.status = 'loading';
      this.pulse = null;
      this.activeTag = null;
      try {
        const res = await ArticleDataService.getSentimentPulse(this.category);
        this.pulse = res.data;
        this.status = 'ready';
      } catch {
        this.status = 'error';
      }
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    clearActiveTag() {
      this.activeTag = null;
      this.tooltipPos = null;
    },
    showTagTooltip(event, i) {
      if (this.activeTag === i) {
        this.activeTag = null;
        this.tooltipPos = null;
        return;
      }
      this.activeTag = i;
      this.$nextTick(() => {
        const tagEl = event.currentTarget;
        const tagRect = tagEl.getBoundingClientRect();
        const tooltipWidth = 220;
        const tooltipHeight = 38;
        const margin = 10;

        // Default: center below the tag
        let left = tagRect.left + tagRect.width / 2 - tooltipWidth / 2;
        let top = tagRect.bottom + 8;
        let showAbove = false;

        // Clamp horizontally within the viewport
        if (left < margin) left = margin;
        if (left + tooltipWidth > window.innerWidth - margin) {
          left = window.innerWidth - tooltipWidth - margin;
        }

        // Flip above if tooltip would overflow the bottom of the viewport
        if (top + tooltipHeight > window.innerHeight - margin) {
          top = tagRect.top - tooltipHeight - 8;
          showAbove = true;
        }

        // Arrow pointer: how far from tooltip left edge is the tag center
        const tagCenterX = tagRect.left + tagRect.width / 2;
        const rawArrow = tagCenterX - left;
        const arrowLeft = Math.min(tooltipWidth - 14, Math.max(10, rawArrow));

        this.tooltipPos = { top, left, showAbove, arrowLeft };
      });
    },
    sentimentColor(score) {
      if (score <= 33) return '#ef4444';
      if (score <= 66) return '#eab308';
      return '#22c55e';
    },
    sentimentColorBg(score) {
      if (score <= 33) return '#fee2e2';
      if (score <= 66) return '#fef9c3';
      return '#dcfce7';
    },
    sentimentLabel(score) {
      if (score <= 33) return 'Negative';
      if (score <= 66) return 'Neutral';
      return 'Positive';
    },
    clampBias(val) {
      return Math.min(100, Math.max(0, val ?? 50));
    },
    tagFontSize(count) {
      if (!this.pulse?.topicCloud?.length) return '1rem';
      const counts = this.pulse.topicCloud.map(t => t.count);
      const min = Math.min(...counts);
      const max = Math.max(...counts);
      const range = max - min || 1;
      const size = 0.75 + ((count - min) / range) * 1.0;
      return `${size.toFixed(2)}rem`;
    },
    capitalize(str) {
      if (!str) return '';
      return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    biasChipStyle(label) {
      const c = BIAS_COLORS[label] || { text: '#374151', bg: '#f3f4f6' };
      return { background: c.bg, color: c.text };
    },
    chipStyle(tag) {
      const baseColor = this.sentimentColor(tag.avgSentiment);
      const followed = this.currentUser && tag.followed;
      return {
        fontSize: this.tagFontSize(tag.count),
        color: followed ? '#fff' : baseColor,
        background: followed ? baseColor : 'transparent',
        borderColor: baseColor,
      };
    },
    async toggleFollowTag(event, i) {
      if (!this.currentUser) {
        this.showTagTooltip(event, i);
        return;
      }
      const tag = this.pulse.topicCloud[i];
      const wasFollowed = tag.followed;
      // Optimistic update
      this.pulse.topicCloud[i] = { ...tag, followed: !wasFollowed };
      try {
        if (wasFollowed) {
          await ArticleDataService.unfollowTopic(tag.tag);
        } else {
          await ArticleDataService.followTopic(tag.tag);
        }
        this.showFollowToast(wasFollowed ? `Unfollowed "${tag.tag}"` : `Following "${tag.tag}"`);
      } catch {
        // Revert on error
        this.pulse.topicCloud[i] = { ...tag, followed: wasFollowed };
        this.showFollowToast(`Could not update "${tag.tag}". Please try again.`, true);
      }
    },
    showFollowToast(message, isError = false) {
      this.followToast = { message, isError };
      setTimeout(() => { this.followToast = null; }, 3000);
    },
  },
};
</script>

<style scoped>
/* ── Root ───────────────────────────────────────────────────── */
.spd {
  box-sizing: border-box;
  width: 100%;
  padding: 0;
  color: #111827;
}

/* ── Header / toggle bar ─────────────────────────────────────── */
.spd__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 10px;
  margin-bottom: 16px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s, box-shadow 0.2s, padding 0.2s, border-radius 0.2s;
}

/* Collapsed state — gives the header a visible, tappable surface */
.spd__header--collapsed {
  background: linear-gradient(135deg, #eef6ff 0%, #f8fafc 100%);
  border: 1px solid rgba(23, 138, 239, 0.25);
  border-left: 4px solid #178aef;
  border-radius: 0 14px 14px 0;
  padding: 12px 16px 12px 14px;
  margin-bottom: 0;
  box-shadow: 0 2px 10px rgba(23, 138, 239, 0.09);
}

.spd__header--collapsed:hover {
  background: linear-gradient(135deg, #e0efff 0%, #f1f8ff 100%);
  box-shadow: 0 4px 16px rgba(23, 138, 239, 0.14);
}

.spd__collapsed-summary {
  margin: 4px 0 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.01em;
}

.spd__header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: nowrap;
}

.spd__chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(23, 138, 239, 0.14);
  border: 1px solid rgba(23, 138, 239, 0.28);
  color: #178aef;
  font-size: 0.75rem;
  transition: transform 0.25s ease, background 0.2s;
  flex-shrink: 0;
}

.spd__chevron--open {
  transform: rotate(180deg);
}

.spd__header:hover .spd__chevron {
  background: rgba(23, 138, 239, 0.24);
}

.spd__eyebrow {
  margin: 0 0 4px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: #0e6abf;
}

.spd__title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.15;
}

.spd__updated {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
  margin-top: 6px;
  white-space: nowrap;
}

.spd__live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: spd-pulse 2s infinite;
  flex-shrink: 0;
}

@keyframes spd-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

/* ── Grid ───────────────────────────────────────────────────── */
.spd__grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    "w1"
    "w2"
    "w3"
    "w4";
  gap: 16px;
}

@media (min-width: 768px) {
  .spd__grid {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "w1 w2"
      "w3 w4";
  }
}

@media (min-width: 1024px) {
  .spd__grid {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "w1 w2"
      "w3 w3"
      "w4 w4";
  }
}

/* ── Card base ──────────────────────────────────────────────── */
.spd__card {
  box-sizing: border-box;
  padding: 20px 22px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 18px;
  background:
    radial-gradient(circle at top left, rgba(23, 138, 239, 0.07), transparent 40%),
    linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.07);
  overflow: hidden;
}

.spd__card--coverage { grid-area: w1; }
.spd__card--sentiment { grid-area: w2; }
.spd__card--topics { grid-area: w3; }
.spd__card--story { grid-area: w4; }

.spd__card-eyebrow {
  margin: 0 0 6px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: #178aef;
}

.spd__card-total {
  margin: 0 0 14px;
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 600;
}

.spd__card-hint {
  margin: -6px 0 12px;
  font-size: 0.72rem;
  line-height: 1.35;
  color: #64748b;
  outline: none;
}

.spd__card-hint:focus {
  color: #334155;
  text-decoration: underline;
}

.spd__provenance {
  margin: 12px 0 0;
  font-size: 0.68rem;
  color: #94a3b8;
  outline: none;
}

.spd__provenance:focus {
  color: #64748b;
  text-decoration: underline;
}

.spd__bias-meter-note {
  font-weight: 500;
  color: #6b7280;
  font-size: 0.85em;
}

.spd__bias-meter-note--solo {
  margin: 12px 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

/* ── Widget 1: Stacked bar (non-politics) ───────────────────── */
.spd__stacked-bar {
  display: flex;
  height: 18px;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 14px;
  gap: 2px;
}

.spd__stack-seg {
  transition: width 0.45s ease;
  border-radius: 999px;
}

.spd__stacked-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.spd__legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
}

.spd__legend-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.spd__legend-label {
  flex: 1;
  font-weight: 600;
  color: #374151;
}

.spd__legend-count {
  color: #6b7280;
  font-size: 0.78rem;
}

/* ── Widget 1: Bias bars (politics) ────────────────────────── */
.spd__bias-bars {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.spd__bias-row {
  display: grid;
  grid-template-columns: 110px 1fr auto;
  align-items: center;
  gap: 10px;
}

.spd__bias-bar-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spd__bias-track {
  height: 12px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
}

.spd__bias-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.45s ease;
  min-width: 4px;
}

.spd__bias-bar-count {
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
}

.spd__bias-bar-pct {
  color: #9ca3af;
  font-weight: 500;
}

/* ── Widget 2: Single category ──────────────────────────────── */
.spd__big-score {
  font-size: 3.6rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.05em;
  margin: 8px 0 6px;
}

.spd__big-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.spd__big-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Bias meter */
.spd__meter-title {
  margin: 18px 0 8px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #374151;
}

.spd__bias-meter {
  margin-top: 4px;
}

.spd__bias-meter-track {
  position: relative;
  height: 14px;
  border-radius: 999px;
  background: linear-gradient(90deg, #1d4ed8, #60a5fa, #9ca3af, #f87171, #dc2626);
  box-shadow: inset 0 0 0 1px rgba(17, 24, 39, 0.1);
  margin-bottom: 6px;
}

.spd__bias-meter-fill {
  display: none; /* marker handles position, fill not needed here */
}

.spd__bias-meter-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #111827;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  transition: left 0.45s ease;
}

.spd__bias-meter-scale {
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  font-weight: 700;
  color: #9ca3af;
  margin-bottom: 4px;
}

.spd__bias-meter-value {
  margin: 6px 0 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #374151;
}

/* ── Widget 2: Global multi-category ────────────────────────── */
.spd__cat-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
}

.spd__cat-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(17, 24, 39, 0.06);
}

.spd__cat-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2937;
}

.spd__cat-badge {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}

.spd__cat-count {
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Widget 3: Topic Cloud ──────────────────────────────────── */
.spd__tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-top: 8px;
  align-items: baseline;
}

.spd__tag {
  font-weight: 700;
  cursor: pointer;
  line-height: 1.2;
  transition: opacity 0.2s, transform 0.15s, background 0.18s, color 0.18s;
  user-select: none;
}

/* Anonymous: plain word cloud, no pill */
.spd__tag:hover,
.spd__tag--active {
  opacity: 0.75;
}

/* Authenticated: proper pill shape */
.spd__tag--auth {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px 4px 10px;
  border-radius: 999px;
  border: 1.5px solid currentColor;
  background: transparent;
  transition: background 0.18s, color 0.18s, transform 0.15s, box-shadow 0.15s;
}

.spd__tag--auth:hover {
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}

.spd__tag--auth:active {
  transform: translateY(0);
}

/* Followed: lift shadow (background/color handled via inline style) */
.spd__tag--followed {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

/* ── Global fixed tooltip (escapes overflow:hidden on cards) ─── */
.spd__global-tooltip {
  position: fixed;
  z-index: 9999;
  white-space: nowrap;
  background: #0f172a;
  color: #f8fafc;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.spd__global-tooltip::before {
  content: '';
  position: absolute;
  top: -5px;
  left: var(--spd-arrow-left, 50%);
  transform: translateX(-50%);
  width: 9px;
  height: 5px;
  background: #0f172a;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
}

.spd__global-tooltip--above::before {
  top: auto;
  bottom: -5px;
  clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
}

/* ── Widget 4: Featured Story ───────────────────────────────── */
.spd__story-title {
  display: block;
  margin: 8px 0 6px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.45;
  text-decoration: none;
}

.spd__story-title:hover {
  color: #178aef;
  text-decoration: underline;
}

.spd__story-link-icon {
  font-size: 0.65rem;
  margin-left: 6px;
  vertical-align: middle;
  color: #9ca3af;
}

.spd__story-source {
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.spd__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.spd__chip {
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: capitalize;
}

/* ── Empty / Error states ────────────────────────────────────── */
.spd__empty {
  margin: 14px 0 0;
  font-size: 0.85rem;
  color: #9ca3af;
  font-style: italic;
}

.spd__error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 24px;
  border-radius: 16px;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #be123c;
  font-size: 0.88rem;
  font-weight: 600;
}

/* ── Skeletons ───────────────────────────────────────────────── */
.spd__skeleton {
  pointer-events: none;
}

.spd__sk {
  border-radius: 8px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: spd-shimmer 1.4s infinite;
  height: 14px;
  margin-bottom: 12px;
}

.spd__sk--short  { width: 40%; }
.spd__sk--mid    { width: 65%; }
.spd__sk--long   { width: 90%; }
.spd__sk--score  { width: 80px; height: 60px; border-radius: 12px; }
.spd__sk--tag    { height: 20px; border-radius: 999px; }

.spd__sk-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

@keyframes spd-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Mobile tweaks ───────────────────────────────────────────── */
@media (max-width: 767px) {
  .spd__card {
    padding: 16px;
    border-radius: 14px;
  }

  .spd__big-score {
    font-size: 2.8rem;
  }

  .spd__bias-row {
    grid-template-columns: 90px 1fr auto;
  }

  .spd__cat-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .spd__cat-count {
    display: none;
  }

  /* Scale title down so it fits on narrow screens */
  .spd__title {
    font-size: 1.1rem;
  }

  /* Expanded state: bare header with bottom rule */
  .spd__header:not(.spd__header--collapsed) {
    padding: 8px 0;
    border-bottom: 1px solid rgba(17, 24, 39, 0.08);
    margin-bottom: 12px;
  }

  /* Collapsed state on mobile: full rounded pill (not half-rounded) */
  .spd__header--collapsed {
    border-radius: 12px;
    padding: 10px 14px 10px 12px;
    margin-bottom: 0;
  }

  /* Hide timestamp in collapsed state on mobile — summary covers the key info */
  .spd__header--collapsed .spd__updated {
    display: none;
  }

  /* Keep timestamp visible when expanded so user knows data is live */
  .spd__header:not(.spd__header--collapsed) .spd__updated {
    display: inline-flex;
  }
}

/* ── Follow icon ─────────────────────────────────────────────── */
.spd__tag-follow-icon {
  display: inline-flex;
  align-items: center;
  font-size: 0.65em;
  opacity: 0.55;
  transition: opacity 0.15s, transform 0.15s;
  flex-shrink: 0;
}

.spd__tag--auth:hover .spd__tag-follow-icon {
  opacity: 1;
  transform: scale(1.2);
}

.spd__tag--followed .spd__tag-follow-icon {
  opacity: 1;
}

/* ── Follow toast ────────────────────────────────────────────── */
.spd__toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #fff;
  padding: 10px 20px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  z-index: 9999;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.spd__toast--error {
  background: #be123c;
}

.spd-toast-enter-active,
.spd-toast-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.spd-toast-enter-from,
.spd-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}
</style>
